import React, { useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  checkActionCode,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../../firebase.config';
import styled from 'styled-components';
import { MailOutlined } from '@ant-design/icons';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  text-align: center;
`;

const IconWrapper = styled.div`
  font-size: 64px;
  color: #9701fc;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const SubTitle = styled.p`
  font-size: 16px;
  color: #bfbdbd;
  max-width: 500px;
  margin-bottom: 1.5rem;
`;

const StyledButton = styled(Button)`
  margin-top: 12px;
`;

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const [confirmed, setConfirmed] = useState(false);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState(null);

  useEffect(() => {
    if (oobCode) {
      checkActionCode(auth, oobCode)
        .then(() => applyActionCode(auth, oobCode))
        .then(() => {
          setConfirmed(true);
        })
        .catch((err) => {
          console.error('Verification error:', err.message);
          setExpired(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [oobCode]);

  const handleResend = async () => {
    if (!auth.currentUser) {
      setResendError('You need to login first.');
      return;
    }

    try {
      await sendEmailVerification(auth.currentUser);
      setResent(true);
    } catch (err) {
      console.error('Resend failed:', err.message);
      setResendError(err.message);
    }
  };

  if (loading) {
    return (
      <Container>
        <Spin size="large" />
      </Container>
    );
  }

  return (
    <Container>
      <IconWrapper>
        <MailOutlined />
      </IconWrapper>

      <Title>
        {confirmed
          ? 'Your email has been successfully verified!'
          : expired
          ? 'Verification link is invalid or expired.'
          : 'Please check your inbox and verify your email.'}
      </Title>

      <SubTitle>
        {confirmed
          ? 'You can now login to your dashboard.'
          : expired
          ? 'The verification link is no longer valid.'
          : "Click the link in your email to verify. Please check your spam folder if you don't see it. Didn't receive it?"}
      </SubTitle>

      {!confirmed && !expired && (
        <>
          <Button
            type="link"
            style={{ color: '#9701fc', fontWeight: 500, fontSize: '16px' }}
            onClick={handleResend}
            disabled={resent}
          >
            {resent ? 'Verification Email Sent' : 'Resend Verification Email'}
          </Button>
          {resendError && (
            <div style={{ color: 'red', marginTop: 8 }}>{resendError}</div>
          )}
        </>
      )}

      <StyledButton
        type="primary"
        size="large"
        onClick={() => navigate('/signin')}
      >
        Login
      </StyledButton>
    </Container>
  );
};

export default EmailVerificationPage;
